import { StyleSheet } from 'react-native';
import React, { useState } from 'react';
import { useThemeContext } from '../../hook/useThemeManager';
import { ColorScheme, theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import {
    Box,
    MenuItem,
    Select,
    SelectChangeEvent,
    SelectProps,
    Chip,
    Typography,
    FormLabel,
    FormControl,
    SxProps,
    Theme,
} from '@mui/material';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import SettingsInputComponentOutlined from '@mui/icons-material/SettingsInputComponentOutlined';
import VideoCameraFrontOutlined from '@mui/icons-material/VideoCameraFrontOutlined';
import PaletteOutlined from '@mui/icons-material/PaletteOutlined';
import TheaterComedyOutlined from '@mui/icons-material/TheaterComedyOutlined';
import MicOutlined from '@mui/icons-material/MicOutlined';
import PersonOutlineOutlined from '@mui/icons-material/PersonOutlineOutlined';
import CheckCircle from '@mui/icons-material/CheckCircle';
import { StyledBox } from '../Box/StylesBox';

export interface IDropdownSelect {
    label: string;
    data: string[];
    maxchipamount?: number;
    multiple?: boolean;
    value?: any;
    labelstyle?: React.CSSProperties;
    placeHolder?: string;
    /** Display-only; option `value` stays the raw string for selection and callbacks. */
    formatOptionLabel?: (option: string) => string;
    onOptionSelected?: (value: string, index: number) => void;
    selectProps?: SelectProps;
    sx?: SxProps<Theme>;
    /** Merged into the select shell (e.g. apply modal black field). */
    selectContainerSx?: SxProps<Theme>;
    /** Merged into the native select `style` (e.g. transparent bg on dark shell). */
    selectDatalistStyle?: React.CSSProperties;
    /** Optional dropdown UI variant. */
    customVariant?: 'default' | 'workProfile';
    onChange?: (e: any) => void;
}

const DropdownSelect: React.FC<IDropdownSelect> = props => {
    const { themeStyle } = useThemeContext();
    const styles = dynamicStyles(themeStyle);
    const {
        label,
        data,
        maxchipamount,
        labelstyle,
        placeHolder,
        formatOptionLabel,
        selectProps,
        sx,
        selectContainerSx,
        selectDatalistStyle,
        customVariant = 'default',
    } = props;

    const displayLabel = (option: string) => (formatOptionLabel ? formatOptionLabel(option) : option);
    const [value, setValue] = useState<any>(placeHolder);
    const [multiValue, setMultiValue] = useState<any>([]);

    const handleChange = (event: SelectChangeEvent<unknown>, child: React.ReactNode) => {
        const {
            target: { value },
        } = event;
        if (selectProps && selectProps.multiple) {
            setMultiValue(typeof value === 'string' ? value.split(',') : value);
        } else {
            setValue(value);
        }
        const selectedIndex = (child as any)?.props?.['data-index'];
        props.onOptionSelected && props.onOptionSelected(value as string, selectedIndex);
    };

    const chipCount = maxchipamount ? maxchipamount : 3;
    const isWorkProfileVariant = customVariant === 'workProfile' && !selectProps?.multiple;
    const getProfileMeta = (option: unknown) => {
        const raw = option == null ? '' : String(option);
        const key = raw.trim().toLowerCase();
        if (key === 'technical') {
            return {
                label: 'Technical',
                subtitle: 'VFX, Sound, Lighting, Direction',
                icon: <SettingsInputComponentOutlined sx={{ fontSize: 20 }} />,
            };
        }
        if (key === 'content_creator') {
            return {
                label: displayLabel(raw),
                subtitle: 'Influencer, Streamer, Digital Media',
                icon: <VideoCameraFrontOutlined sx={{ fontSize: 20 }} />,
            };
        }
        if (key === 'artist') {
            return {
                label: 'Artist',
                subtitle: 'Design, Illustration, Set Design',
                icon: <PaletteOutlined sx={{ fontSize: 20 }} />,
            };
        }
        if (key === 'performer') {
            return {
                label: 'Performer',
                subtitle: 'Actor, Stunt, Background, Voice',
                icon: <TheaterComedyOutlined sx={{ fontSize: 20 }} />,
            };
        }
        if (key === 'voiceover') {
            return {
                label: 'Voiceover',
                subtitle: 'Narration, Ads, Dubbing',
                icon: <MicOutlined sx={{ fontSize: 20 }} />,
            };
        }
        return {
            label: displayLabel(raw),
            subtitle: '',
            icon: <PersonOutlineOutlined sx={{ fontSize: 20 }} />,
        };
    };
    return (
        <Box
            sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                ...sx,
            }}>
            {label && <FormLabel style={{ ...styles.text, ...labelstyle }}>{label}</FormLabel>}
            <StyledBox
                style={styles.flexRow}
                sx={{
                    backgroundColor: '#1a1a1a',
                    border: isWorkProfileVariant ? '1px solid rgba(72,72,71,0.55)' : '1px solid #484847',
                    borderRadius: isWorkProfileVariant ? '14px' : '8px',
                    position: 'relative',
                    minHeight: isWorkProfileVariant ? '52px' : '50px',
                    paddingTop: isWorkProfileVariant ? '6px' : '10px',
                    paddingRight: '14px',
                    paddingLeft: '14px',
                    paddingBottom: isWorkProfileVariant ? '6px' : '10px',
                    transition: 'border-color 0.2s ease, background-color 0.2s ease',
                    '&:hover': {
                        borderColor: `${theme[themeStyle].highlight}99`,
                        backgroundColor: `${theme[themeStyle].highlight}14`,
                    },
                    ...selectContainerSx,
                }}>
                <FormControl fullWidth>
                    <Select
                        variant="standard"
                        disableUnderline
                        name={label}
                        {...selectProps}
                        value={
                            selectProps?.multiple
                                ? multiValue
                                : selectProps?.value !== undefined
                                ? selectProps.value
                                : value
                        }
                        onChange={handleChange}
                        style={{ ...styles.datalist, ...selectDatalistStyle }}
                        sx={{
                            '&:hover': { cursor: 'pointer' },
                            '& .MuiSelect-select': {
                                display: 'flex',
                                alignItems: 'center',
                                minHeight: isWorkProfileVariant ? '38px !important' : undefined,
                                paddingTop: isWorkProfileVariant ? '0 !important' : undefined,
                                paddingBottom: isWorkProfileVariant ? '0 !important' : undefined,
                                paddingRight: isWorkProfileVariant ? '40px !important' : undefined,
                            },
                            '& .MuiSvgIcon-root': {
                                color: theme[themeStyle].highlight,
                            },
                        }}
                        IconComponent={props => (
                            <KeyboardArrowDown
                                {...props}
                                sx={{
                                    color: `${theme[themeStyle].highlight} !important`,
                                    fontSize: isWorkProfileVariant ? 20 : 22,
                                    right: 4,
                                }}
                            />
                        )}
                        renderValue={(selected: any) =>
                            selectProps && selectProps.multiple ? (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.slice(0, maxchipamount).map((value: any) => (
                                        <Chip
                                            key={value}
                                            label={value}
                                            sx={{
                                                fontFamily: FONT_ENUM.REGULAR_HEADER,
                                                fontSize: 8,
                                                color: '#fff',
                                                backgroundColor: 'rgb(54, 58, 61)',
                                                textTransform: 'uppercase',
                                                marginLeft: 0.5,
                                            }}
                                        />
                                    ))}
                                    {selected.length > chipCount && (
                                        <Chip
                                            key={value}
                                            label={`+${selected.length - chipCount}`}
                                            sx={{
                                                fontFamily: FONT_ENUM.REGULAR_HEADER,
                                                fontSize: 8,
                                                color: '#fff',
                                                backgroundColor: 'rgb(54, 58, 61)',
                                                textTransform: 'uppercase',
                                                marginLeft: 0.5,
                                            }}
                                        />
                                    )}
                                </Box>
                            ) : (
                                <>
                                    {isWorkProfileVariant && typeof selected === 'string' ? (
                                        (() => {
                                            const meta = getProfileMeta(selected);
                                            return (
                                                <Box
                                                    sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                                    <Box
                                                        sx={{
                                                            width: 30,
                                                            height: 30,
                                                            borderRadius: '7px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            flexShrink: 0,
                                                            backgroundColor: `${theme[themeStyle].highlight}26`,
                                                            color: theme[themeStyle].highlight,
                                                            '& .MuiSvgIcon-root': { fontSize: 18 },
                                                        }}>
                                                        {meta.icon}
                                                    </Box>
                                                    <Typography
                                                        noWrap
                                                        sx={{
                                                            color: theme[themeStyle].lightWhite,
                                                            fontSize: 15,
                                                            fontWeight: 700,
                                                            lineHeight: 1.1,
                                                            fontFamily: FONT_ENUM.BOLD_HEADER,
                                                            letterSpacing: '-0.01em',
                                                            minWidth: 0,
                                                        }}>
                                                        {meta.label}
                                                    </Typography>
                                                </Box>
                                            );
                                        })()
                                    ) : (
                                        <Typography style={styles.option}>
                                            {typeof selected === 'string' ? displayLabel(selected) : selected}
                                        </Typography>
                                    )}
                                </>
                            )
                        }
                        MenuProps={{
                            PaperProps: {
                                sx: {
                                    bgcolor: '#1a1a1a',
                                    border: isWorkProfileVariant
                                        ? '1px solid rgba(72,72,71,0.25)'
                                        : '1px solid #484847',
                                    borderRadius: isWorkProfileVariant ? '12px' : '8px',
                                    p: isWorkProfileVariant ? 0.75 : 0,
                                    mt: 0.5,
                                    maxWidth: isWorkProfileVariant ? 'min(100vw - 16px, 320px)' : undefined,
                                    overflow: 'hidden',
                                    boxSizing: 'border-box',
                                    boxShadow: isWorkProfileVariant
                                        ? '0 18px 40px rgba(0,0,0,0.55)'
                                        : '0 8px 24px rgba(0,0,0,0.45)',
                                    '& .MuiMenuItem-root': {
                                        padding: isWorkProfileVariant ? '10px 10px' : '10px 16px',
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        fontSize: isWorkProfileVariant ? 14 : 16,
                                        color: theme[themeStyle].lightWhite,
                                        borderRadius: isWorkProfileVariant ? '8px' : 0,
                                        marginBottom: isWorkProfileVariant ? '4px' : 0,
                                        '&:hover': {
                                            backgroundColor: isWorkProfileVariant
                                                ? '#262626'
                                                : `${theme[themeStyle].highlight}1a`,
                                        },
                                        '&.Mui-selected': {
                                            backgroundColor: isWorkProfileVariant
                                                ? `${theme[themeStyle].highlight}1f`
                                                : `${theme[themeStyle].highlight}26`,
                                            border: isWorkProfileVariant
                                                ? `1px solid ${theme[themeStyle].highlight}66`
                                                : undefined,
                                            boxShadow: isWorkProfileVariant
                                                ? `0 0 0 1px ${theme[themeStyle].highlight}33`
                                                : undefined,
                                            '&:hover': {
                                                backgroundColor: isWorkProfileVariant
                                                    ? `${theme[themeStyle].highlight}29`
                                                    : `${theme[themeStyle].highlight}33`,
                                            },
                                        },
                                        '&:last-of-type': {
                                            marginBottom: 0,
                                        },
                                    },
                                },
                            },
                        }}>
                        {data.map((option, index) => (
                            <MenuItem value={option} style={styles.option} key={index} data-index={index}>
                                {isWorkProfileVariant ? (
                                    <Box
                                        sx={{
                                            width: '100%',
                                            maxWidth: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 0.75,
                                            minWidth: 0,
                                        }}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                minWidth: 0,
                                                flex: 1,
                                            }}>
                                            <Box
                                                sx={{
                                                    width: 34,
                                                    height: 34,
                                                    borderRadius: '8px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0,
                                                    bgcolor:
                                                        option === (selectProps?.value as string)
                                                            ? theme[themeStyle].highlight
                                                            : '#0e0e0e',
                                                    color:
                                                        option === (selectProps?.value as string)
                                                            ? theme[themeStyle].black
                                                            : theme[themeStyle].tag_white,
                                                    '& .MuiSvgIcon-root': { fontSize: 19 },
                                                }}>
                                                {getProfileMeta(option).icon}
                                            </Box>
                                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                                <Typography
                                                    noWrap
                                                    sx={{
                                                        fontFamily: FONT_ENUM.BOLD_HEADER,
                                                        fontWeight: 700,
                                                        fontSize: 15,
                                                        lineHeight: 1.1,
                                                        color:
                                                            option === (selectProps?.value as string)
                                                                ? theme[themeStyle].highlight
                                                                : theme[themeStyle].lightWhite,
                                                    }}>
                                                    {getProfileMeta(option).label}
                                                </Typography>
                                                {getProfileMeta(option).subtitle ? (
                                                    <Typography
                                                        noWrap
                                                        sx={{
                                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                            fontSize: 10,
                                                            mt: 0.25,
                                                            color: 'rgba(255,255,255,0.58)',
                                                        }}>
                                                        {getProfileMeta(option).subtitle}
                                                    </Typography>
                                                ) : null}
                                            </Box>
                                        </Box>
                                        {option === (selectProps?.value as string) ? (
                                            <CheckCircle
                                                sx={{
                                                    color: theme[themeStyle].highlight,
                                                    fontSize: 18,
                                                    flexShrink: 0,
                                                }}
                                            />
                                        ) : null}
                                    </Box>
                                ) : (
                                    displayLabel(option)
                                )}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </StyledBox>
        </Box>
    );
};

DropdownSelect.defaultProps = {
    maxchipamount: 3,
};

const dynamicStyles = (colorScheme: ColorScheme) =>
    StyleSheet.create({
        text: {
            textAlign: 'left',
            fontSize: 14,
            fontStyle: 'normal',
            fontWeight: '400',
            color: theme[colorScheme].form_label,
            fontFamily: FONT_ENUM.MAIN_REGULAR,
        },
        flexRow: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            borderRadius: 8,
        },
        option: {
            color: theme[colorScheme].lightWhite,
            fontSize: 16,
            cursor: 'pointer',
            fontFamily: FONT_ENUM.MAIN_REGULAR,
            fontWeight: '500',
        },
        absolute: {
            position: 'absolute',
            top: '0px',
            right: '0px',
            left: '0px',
            width: 'inherit',
            height: 'auto',
            zIndex: 100,
            backgroundColor: theme[colorScheme].gray_text,
        },
        datalist: {
            backgroundColor: 'transparent',
            height: '100%',
            width: '100%',
            border: 'none',
            borderRadius: 8,
            WebkitCalendarPickerIndicator: 'none',
            color: theme[colorScheme].lightWhite,
            position: 'relative',
        },
    });

export { DropdownSelect };
